import { generatePath } from 'react-router-dom';

import { sessionStorageWrapper } from 'approot/shared/storage';
import { isStaleSessionModalOpenVar } from 'lib/session-stale-monitor.apollo';
import {
  resetAndExtendSession,
  setSessionExpiryTime,
} from './session-storage-duration';
import { signOut, studentSignOut } from 'approot/shared/signin/signin.utils';
import { PUBLIC_HOME, PUBLIC_STUDENTS } from 'global/public-routes.constants';
import { navigateTo } from './session-navigation';
import { isValidStudentRedirectPath } from 'approot/shared/signin/logic/is-valid-student-redirect-path';
import {
  PRIVATE_CLASS,
  PRIVATE_STUDENT_CLASSES,
} from 'global/private-routes.constants';
import { getRegionalisedLink } from 'approot/shared/logic/get-regionalised-link';
import { authChannel } from './session-broadcast';
import { getStudentData } from 'approot/student-class-to-do/student-class-to-do.utils';
import { isValidStudentClassToDoPath } from 'approot/shared/signin/logic/is-valid-student-class-to-do-path';
import { isInteractivePath } from 'approot/shared/signin/logic/is-interactive-path';
import { getSessionToken } from './get-session-token';

const SIGN_IN_REDIRECTION_PATH_KEY = 'signinRedirectionPath';
const EXTEND_SESSION_EVENT = 'EXTEND_SESSION';
const SIGN_OUT_EVENT = 'SIGN_OUT';
const SIGN_IN_EVENT = 'SIGN_IN';

type AuthEvent = {
  type:
    | typeof EXTEND_SESSION_EVENT
    | typeof SIGN_OUT_EVENT
    | typeof SIGN_IN_EVENT;
  userId?: number;
  token: string;
  newExpiryTime?: number;
  isTeacher: boolean;
};

export function emitTeacherSignoutStorageEvent(userId: number, token: string) {
  authChannel.postMessage({
    type: SIGN_OUT_EVENT,
    userId: userId,
    token: token,
    isTeacher: true,
  });
}

export function emitStudentSignoutStorageEvent(token: string) {
  authChannel.postMessage({
    type: SIGN_OUT_EVENT,
    token: token,
    isTeacher: false,
  });
}

export function emitTeacherExtendSessionStorageEvent(token: string) {
  const newExpiryTime = resetAndExtendSession();
  isStaleSessionModalOpenVar(false);
  emitExtendSessionStorageEvent({
    token,
    newExpiryTime,
    isTeacher: true,
  });
}

export function emitTeacherSigninStorageEvent(token: string) {
  authChannel.postMessage({
    type: SIGN_IN_EVENT,
    token: token,
    isTeacher: true,
  });
}

export function emitStudentSigninStorageEvent(token: string) {
  authChannel.postMessage({
    type: SIGN_IN_EVENT,
    token: token,
    isTeacher: false,
  });
}

export function emitStudentExtendSessionStorageEvent(token: string) {
  const newExpiryTime = resetAndExtendSession();
  isStaleSessionModalOpenVar(false);
  emitExtendSessionStorageEvent({
    token,
    newExpiryTime,
    isTeacher: false,
  });
}

export function emitExtendSessionStorageEvent({
  token,
  newExpiryTime,
  isTeacher,
}: {
  token: string;
  newExpiryTime: number;
  isTeacher: boolean;
}) {
  authChannel.postMessage({
    type: EXTEND_SESSION_EVENT,
    token: token,
    newExpiryTime: newExpiryTime,
    isTeacher: isTeacher,
  });
}

export function getSigninRedirectionPathFromSession() {
  return sessionStorageWrapper.getItem(SIGN_IN_REDIRECTION_PATH_KEY);
}

export function setSigninRedirectionPathForSession(redirectPath: string = '') {
  sessionStorageWrapper.setItem(
    SIGN_IN_REDIRECTION_PATH_KEY,
    redirectPath
      ? redirectPath
      : `${window.location.pathname}${window.location.search}`.replace(
          `/${process.env.REACT_APP_REGION}`,
          ''
        )
  );
}

function handleExtendSessionEvent(authEvent: AuthEvent) {
  const eventToken = authEvent.token;
  const token = getSessionToken();
  if (token === eventToken && authEvent.newExpiryTime) {
    setSessionExpiryTime(authEvent.newExpiryTime);
    isStaleSessionModalOpenVar(false);
  }
}

function handleSignOutEvent(authEvent: AuthEvent) {
  if (authEvent.isTeacher) {
    signOut({
      signOutServerSide: false,
      emitStorageEvent: false,
    });
  } else {
    studentSignOut();
    navigateTo(PUBLIC_STUDENTS, { replace: true });
  }

  isStaleSessionModalOpenVar(false);
}

function handleSignInEvent(authEvent: AuthEvent) {
  const redirectPath = getSigninRedirectionPathFromSession();
  if (authEvent.isTeacher) {
    // Handle interactive path redirect for teachers and student signins or signups after interactive Ebook mentor text access
    if (isInteractivePath(redirectPath)) {
      window.location.replace(getRegionalisedLink(redirectPath ?? PUBLIC_HOME));
    } else if (isValidStudentRedirectPath(redirectPath)) {
      window.location.replace(getRegionalisedLink(PUBLIC_HOME));
    } else {
      window.location.replace(getRegionalisedLink(redirectPath ?? PUBLIC_HOME));
    }
    return;
  }

  // student class to do unauthenticated access
  const studentData = getStudentData();
  if (studentData.classId) {
    if (redirectPath && isValidStudentClassToDoPath(redirectPath)) {
      window.location.replace(getRegionalisedLink(redirectPath));
    } else {
      window.location.replace(
        getRegionalisedLink(
          generatePath(PRIVATE_CLASS, {
            classId: studentData.classId || '',
          })
        )
      );
    }
    return;
  }

  // authenticated student access
  if (isValidStudentRedirectPath(redirectPath)) {
    window.location.replace(
      getRegionalisedLink(redirectPath ?? PRIVATE_STUDENT_CLASSES)
    );
  } else {
    window.location.replace(getRegionalisedLink(PRIVATE_STUDENT_CLASSES));
  }
}

authChannel.onmessage = event => {
  const authEvent = event.data;
  if (authEvent?.type === EXTEND_SESSION_EVENT) {
    handleExtendSessionEvent(authEvent);
  } else if (authEvent?.type === SIGN_OUT_EVENT) {
    handleSignOutEvent(authEvent);
  } else if (authEvent?.type === SIGN_IN_EVENT) {
    handleSignInEvent(authEvent);
  }
};
